import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogMedia,
  AlertDialogTitle,
} from "@bowfast/ui/components/alert-dialog";
import { Avatar, AvatarFallback, AvatarImage } from "@bowfast/ui/components/avatar";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@bowfast/ui/components/breadcrumb";
import { Button } from "@bowfast/ui/components/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@bowfast/ui/components/dropdown-menu";
import { SidebarTrigger } from "@bowfast/ui/components/sidebar";
import { Tooltip, TooltipContent, TooltipTrigger } from "@bowfast/ui/components/tooltip";
import { Link } from "@tanstack/react-router";
import {
  CheckIcon,
  ChevronsUpDownIcon,
  LogOutIcon,
  MapPinIcon,
  MonitorIcon,
  MoonIcon,
  SearchIcon,
  SettingsIcon,
  SunIcon,
  UserIcon,
} from "lucide-react";
import { Fragment, useState } from "react";
import { NotificationsTray } from "~/components/notifications/NotificationsTray";
import type { ThemeMode } from "~/hooks/use-theme";
import { useTheme } from "~/hooks/use-theme";

export interface AppHeaderCrumb {
  label: string;
  href?: string;
}

export interface AppHeaderUser {
  name: string;
  /** Org roles — shown as plain text joined by " · " */
  roles: string[];
  email: string;
  avatar: string;
}

// ── Theme switcher ────────────────────────────────────────────────────────────

const THEME_OPTIONS: { value: ThemeMode; label: string; Icon: typeof SunIcon }[] = [
  { value: "light", label: "Light", Icon: SunIcon },
  { value: "dark", label: "Dark", Icon: MoonIcon },
  { value: "auto", label: "Auto", Icon: MonitorIcon },
];

function ThemeSwitcher() {
  const { theme, setTheme } = useTheme();
  const [showSunDialog, setShowSunDialog] = useState(false);
  const current = THEME_OPTIONS.find((o) => o.value === theme) ?? THEME_OPTIONS[0]!;
  const CurrentIcon = current.Icon;

  function handleSelectAuto() {
    if (typeof navigator === "undefined" || !navigator.permissions) {
      setTheme("auto");
      return;
    }
    navigator.permissions
      .query({ name: "geolocation" })
      .then((status) => {
        if (status.state === "prompt") {
          setShowSunDialog(true);
        } else {
          setTheme("auto");
        }
      })
      .catch(() => setTheme("auto"));
  }

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger
          render={<Button variant="ghost" size="icon" aria-label={`Appearance: ${current.label}`} />}
        >
          <CurrentIcon />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" sideOffset={4} className="min-w-40 rounded-lg">
          <DropdownMenuGroup>
            <DropdownMenuLabel>Appearance</DropdownMenuLabel>
          </DropdownMenuGroup>
          <DropdownMenuSeparator />
          {THEME_OPTIONS.map(({ value, label, Icon }) => (
            <DropdownMenuItem key={value} onClick={value === "auto" ? handleSelectAuto : () => setTheme(value)}>
              <Icon />
              {label}
              {theme === value && <CheckIcon className="ml-auto size-3.5" />}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Geolocation explanation dialog — shown when user picks Auto and permission is "prompt" */}
      <AlertDialog open={showSunDialog} onOpenChange={setShowSunDialog}>
        <AlertDialogContent size="md">
          <AlertDialogHeader>
            <AlertDialogMedia>
              <MapPinIcon />
            </AlertDialogMedia>
            <AlertDialogTitle>Sail by the sun</AlertDialogTitle>
            <AlertDialogDescription>
              In Auto mode Bowfast reads sunrise and sunset times for your exact position and shifts the theme at dawn
              and dusk — so the interface feels right whether you're on a noon watch or a night passage. No position
              data leaves your device.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Not now</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setTheme("auto");
                setShowSunDialog(false);
              }}
            >
              Allow location
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

// ── AppHeader ─────────────────────────────────────────────────────────────────

export function AppHeader({ breadcrumbs, user }: Readonly<{ breadcrumbs: AppHeaderCrumb[]; user: AppHeaderUser }>) {
  const initials = user.name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  return (
    <header className="sticky top-0 z-20 flex h-16 shrink-0 items-center gap-2 border-b border-border bg-background px-4 transition-[width] ease-linear">
      {/* ── Left: sidebar trigger + separator + breadcrumb ── */}
      <div className="flex flex-1 min-w-0 items-center gap-2">
        <SidebarTrigger className="-ml-1" />
        <div aria-hidden="true" className="h-4 w-px shrink-0 bg-border" />
        <Breadcrumb className="min-w-0">
          <BreadcrumbList className="flex-nowrap overflow-hidden min-w-0">
            {breadcrumbs.map((crumb, i) => (
              <Fragment key={crumb.href ?? crumb.label}>
                <BreadcrumbItem>
                  {i < breadcrumbs.length - 1 ? (
                    <BreadcrumbLink href={crumb.href ?? "#"}>{crumb.label}</BreadcrumbLink>
                  ) : (
                    <BreadcrumbPage className="block truncate min-w-0">{crumb.label}</BreadcrumbPage>
                  )}
                </BreadcrumbItem>
                {i < breadcrumbs.length - 1 && <BreadcrumbSeparator />}
              </Fragment>
            ))}
          </BreadcrumbList>
        </Breadcrumb>
      </div>

      {/* ── Right: utilities + identity ── */}
      <div className="flex items-center gap-1">
        <Tooltip>
          <TooltipTrigger render={<Button variant="ghost" size="icon" aria-label="Search" />}>
            <SearchIcon />
          </TooltipTrigger>
          <TooltipContent>
            Search <kbd className="ml-1 rounded border border-border bg-muted px-1 text-[0.625rem] font-mono">⌘K</kbd>
          </TooltipContent>
        </Tooltip>

        <NotificationsTray />

        <ThemeSwitcher />

        <div aria-hidden="true" className="mx-1 h-4 w-px shrink-0 bg-border" />

        {/* User menu — h-12 + default avatar to match sidebar nav-user comfort */}
        <DropdownMenu>
          <DropdownMenuTrigger render={<Button variant="ghost" className="h-12 gap-2.5 px-3 data-open:bg-muted" />}>
            <Avatar shape="square">
              <AvatarImage src={user.avatar} alt={user.name} />
              <AvatarFallback>{initials}</AvatarFallback>
            </Avatar>
            <div className="hidden min-w-0 flex-col text-left leading-tight md:flex">
              <span className="max-w-36 truncate text-sm font-medium">{user.name}</span>
              <span className="max-w-36 truncate text-xs text-muted-foreground">{user.roles.join(" · ")}</span>
            </div>
            <ChevronsUpDownIcon className="hidden size-4 text-muted-foreground md:block" />
          </DropdownMenuTrigger>

          <DropdownMenuContent align="end" sideOffset={4} className="min-w-52 rounded-lg">
            <DropdownMenuGroup>
              <DropdownMenuLabel className="p-0 font-normal">
                <div className="flex items-center gap-2 px-1 py-1.5 text-sm">
                  <Avatar shape="square" size="sm">
                    <AvatarImage src={user.avatar} alt={user.name} />
                    <AvatarFallback>{initials}</AvatarFallback>
                  </Avatar>
                  <div className="flex min-w-0 flex-col text-left leading-tight">
                    <span className="truncate font-medium">{user.name}</span>
                    <span className="truncate text-xs text-muted-foreground">{user.roles.join(" · ")}</span>
                  </div>
                </div>
              </DropdownMenuLabel>
            </DropdownMenuGroup>

            <DropdownMenuSeparator />

            <DropdownMenuGroup>
              <DropdownMenuItem>
                <UserIcon />
                Profile
              </DropdownMenuItem>
              <DropdownMenuItem render={<Link to="/settings" />}>
                <SettingsIcon />
                Settings
              </DropdownMenuItem>
            </DropdownMenuGroup>

            <DropdownMenuSeparator />

            <DropdownMenuItem render={<Link to="/auth/logout" />} className="text-destructive focus:text-destructive">
              <LogOutIcon />
              Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
